import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ChatText, EnvelopeSimple, WhatsappLogo, FacebookLogo, XLogo, LinkedinLogo, Gift } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtExpiry, money } from "@/lib/api";
import { PageTitle, Stat, Empty } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { LeaderboardOptIn } from "@/components/LeaderboardOptIn";

const STEPS = ["signed_up", "paid", "attended", "rewarded"];
const stepOf = (r) => (r.status === "rewarded" ? 4 : r.attended_at ? 3 : ["paid", "on_hold"].includes(r.status) ? 2 : 1);

const Share = ({ code, messages }) => {
  const [msg, setMsg] = useState(0);
  const link = (ch) => `${window.location.origin}/r/${code}?ch=${ch}`;
  const text = (ch) => messages[msg].replace("{link}", link(ch));
  const enc = encodeURIComponent;
  const btns = [
    ["sms", "Text", ChatText, `sms:?&body=${enc(text("sms"))}`],
    ["email", "Email", EnvelopeSimple, `mailto:?subject=${enc("Come to Mission Control Trading with me")}&body=${enc(text("email"))}`],
    ["whatsapp", "WhatsApp", WhatsappLogo, `https://wa.me/?text=${enc(text("whatsapp"))}`],
    ["facebook", "Facebook", FacebookLogo, `https://www.facebook.com/sharer/sharer.php?u=${enc(link("facebook"))}`],
    ["x", "X", XLogo, `https://twitter.com/intent/tweet?text=${enc(text("x"))}`],
    ["linkedin", "LinkedIn", LinkedinLogo, `https://www.linkedin.com/sharing/share-offsite/?url=${enc(link("linkedin"))}`],
  ];
  const copy = async () => { await navigator.clipboard.writeText(link("link")); toast.success("Link copied"); };
  return (
    <div className="panel grid gap-5 p-5 md:grid-cols-[auto,1fr]" data-testid="referral-share-panel">
      <div className="flex flex-col items-center gap-2"><QRCodeSVG value={link("qr")} size={140} fgColor="#0B1B33" /><span className="font-mono-num text-lg font-semibold" data-testid="referral-code">{code}</span></div>
      <div className="space-y-3">
        <div className="flex gap-2"><Input readOnly value={link("link")} className="font-mono-num text-xs" data-testid="referral-link-input" /><button className="btn-navy" onClick={copy} data-testid="referral-copy-btn"><Copy /> Copy</button></div>
        <div>
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-[#475569]">Approved message</div>
          <select value={msg} onChange={(e) => setMsg(Number(e.target.value))} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="referral-message-select">
            {messages.map((m, i) => <option key={i} value={i}>{m.replace("{link}", "[your link]").slice(0, 90)}…</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          {btns.map(([ch, label, Icon, href]) => <a key={ch} href={href} target="_blank" rel="noreferrer" className="btn-outline px-3 py-2 text-sm" data-testid={`share-${ch}-btn`}><Icon size={18} /> {label}</a>)}
        </div>
        <p className="text-xs text-[#475569]">Share only with people you know. No profit or income claims.</p>
      </div>
    </div>
  );
};

export default function StudentReferrals() {
  const { data: d, loading, reload } = useFetch("/me/referrals");
  const [greet, setGreet] = useState(null);
  if (loading) return <Loader />;
  const accept = async () => { try { await api.post("/me/referrals/accept-terms"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const saveGreet = async () => { await api.put("/me/referrals/greeting", { greeting: greet }); toast.success("Greeting saved"); reload(); };
  const r = d.rules;
  const offer = r.friend_offer_type === "percent" ? `${r.friend_offer_value}% off` : `${money(r.friend_offer_value)} off`;
  if (!d.terms_accepted) return (
    <>
      <PageTitle title="Bring a friend" />
      <div className="panel max-w-2xl p-6" data-testid="referral-terms-gate">
        <Gift size={40} weight="duotone" className="text-[#E0A21B]" />
        <h2 className="mt-3 font-display text-3xl font-bold uppercase">Your friend gets {offer}. You get {money(r.reward_cents)} credit.</h2>
        <p className="mt-2 text-sm text-[#475569]">You earn your reward when your friend checks in at class. Bonus rewards at {r.tiers.map((t) => t.count).join(", ")} friends.</p>
        <p className="mt-4 text-sm">Before your link works, please read and accept the <Link to="/referral-terms" className="font-semibold underline" target="_blank">referral terms</Link>.</p>
        <button className="btn-gold mt-4" onClick={accept} data-testid="referral-accept-terms-btn">I accept, give me my link</button>
      </div>
      {(d.balance_cents > 0 || d.credit_history.length > 0) && <CreditHistory d={d} />}
    </>
  );
  const pct = d.next_tier ? (100 * d.rewarded) / d.next_tier.count : 100;
  return (
    <>
      <PageTitle title="Bring a friend" sub={`Friend gets ${offer} their first class. You earn ${money(r.reward_cents)} credit when they attend.`} />
      {d.paused && <div className="panel mb-4 border-l-4 border-l-[#C8382E] p-4 text-sm" data-testid="referral-paused">Your referral link is paused. Contact support.</div>}
      {!d.reward_eligible && <div className="panel mb-4 border-l-4 border-l-[#E0A21B] p-4 text-sm" data-testid="referral-not-eligible">You can share now. Rewards are released once you have bought a ticket yourself.</div>}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Link clicks" value={d.clicks} testid="ref-stat-clicks" />
        <Stat label="Friends signed up" value={d.referrals.length} testid="ref-stat-signups" />
        <Stat label="Friends attended" value={d.attended} accent="#1E8E5A" testid="ref-stat-attended" />
        <Stat label="Credit balance" value={money(d.balance_cents)} accent="#1E8E5A" testid="ref-stat-balance" />
      </div>
      <div className="mt-4"><Share code={d.code} messages={r.share_messages} /></div>
      <LeaderboardOptIn />
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="panel p-5">
          <h3 className="font-display text-xl font-bold uppercase">Next tier</h3>
          {d.next_tier ? <><p className="mt-2 text-sm">{d.rewarded} / {d.next_tier.count} friends: <strong>{d.next_tier.reward}</strong></p><Progress value={pct} className="mt-2" /></> : <p className="mt-2 text-sm">All tiers unlocked.</p>}
          <div className="mt-4 space-y-1 text-xs text-[#475569]">{r.tiers.map((t) => <div key={t.count}>{t.count} friends: {t.reward}</div>)}</div>
        </div>
        <div className="panel space-y-2 p-5 lg:col-span-2">
          <h3 className="font-display text-xl font-bold uppercase">Personal greeting</h3>
          <Input maxLength={160} placeholder="e.g. I loved this class, come with me to Calgary!" value={greet ?? d.greeting} onChange={(e) => setGreet(e.target.value)} data-testid="referral-greeting-input" />
          <button className="btn-outline" onClick={saveGreet} disabled={greet == null} data-testid="referral-greeting-save-btn">Save greeting</button>
        </div>
      </div>
      <h3 className="mb-2 mt-6 font-display text-2xl font-bold uppercase">Your invites</h3>
      {d.referrals.length === 0 ? <Empty>No friends yet. Share your link to get started.</Empty> : (
        <div className="space-y-2">
          {d.referrals.map((x) => (
            <div key={x.id} className="panel p-4" data-testid={`invite-row-${x.id}`}>
              <div className="flex flex-wrap justify-between gap-2 text-sm"><span className="font-semibold">{x.friend_name}</span><span className="text-xs uppercase text-[#475569]">{x.status === "reversed" ? "Reversed" : x.status === "blocked" ? "Not eligible" : x.status === "on_hold" ? `On hold: ${x.hold_reason || ""}` : `Joined ${fmtDate(x.created_at)}`}</span></div>
              <div className="mt-3 grid grid-cols-5 gap-1 text-[10px] font-bold uppercase tracking-wider">
                {["Clicked", ...STEPS.map((s) => s.replace("_", " "))].map((s, i) => (
                  <div key={s}><div className={`h-1.5 rounded-full ${i <= stepOf(x) && !["reversed", "blocked"].includes(x.status) ? "bg-[#1E8E5A]" : "bg-[#E2E8F0]"}`} /><div className="mt-1 text-[#475569]">{s === "rewarded" ? "Reward" : s}</div></div>
                ))}
              </div>
              {x.reward_cents > 0 && x.status === "rewarded" && <div className="mt-2 text-sm font-semibold text-[#1E8E5A]">+{money(x.reward_cents)} credit</div>}
            </div>
          ))}
        </div>
      )}
      <CreditHistory d={d} />
    </>
  );
}

function CreditHistory({ d }) {
  return (
    <>
      <h3 className="mb-2 mt-6 font-display text-2xl font-bold uppercase">Credit history <span className="font-mono-num text-base text-[#1E8E5A]" data-testid="credit-balance">{money(d.balance_cents)}</span></h3>
      {d.credit_history.length === 0 ? <Empty>No credit yet. Credit lasts {d.rules.expiry_months} months and is applied at checkout.</Empty> : (
        <div className="panel divide-y">{d.credit_history.map((c) => <div key={c.id} className="flex justify-between p-3 text-sm"><span>{c.note}{c.expires_at && ["earned", "restored"].includes(c.type) && <span className="text-xs text-[#475569]"> · expires {fmtExpiry(c.expires_at)}</span>}</span><span className={`font-mono-num ${c.amount_cents < 0 || c.type === "forfeited" ? "text-[#C8382E]" : "text-[#1E8E5A]"}`}>{c.type === "forfeited" ? `${money(c.forfeited_cents)} not returned` : `${c.amount_cents < 0 ? "−" : "+"}${money(Math.abs(c.amount_cents))}`}</span></div>)}</div>
      )}
    </>
  );
}
